import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function CarFreeVancouverCanvas() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "car-free-adventures-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-getting-around": "Getting Around Car-Free",
    "section-top-destinations": "Top Car-Free Destinations",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.translink.ca",
      label: "TransLink Trip Planner (Official Metro Vancouver Transit)",
    },
    {
      url: "https://www.theaquabus.com",
      label: "Aquabus Ferry Service in False Creek",
    },
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver (Tourism Board)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <p> (or <li>) with ref={(el) => setSpotRef(id, el)}.
  // Coordinates below match the conversation's details (~ 4 decimals).
  const locations = [
    {
      id: "stanley-park",
      lat: 49.3042,
      lng: -123.1443,
      popupText: "Stanley Park: Vancouver's famous urban park",
    },
    {
      id: "granville-island",
      lat: 49.2726,
      lng: -123.1352,
      popupText: "Granville Island: Market & Arts Hub",
    },
    {
      id: "gastown",
      lat: 49.2844,
      lng: -123.1090,
      popupText: "Gastown: Historic District",
    },
    {
      id: "kitsilano-beach",
      lat: 49.2739,
      lng: -123.1551,
      popupText: "Kitsilano Beach: Popular Local Hangout",
    },
    {
      id: "grouse-mountain",
      lat: 49.3772,
      lng: -123.0816,
      popupText: "Grouse Mountain: Outdoor Adventures & Scenic Views",
    },
    {
      id: "capilano",
      lat: 49.3429,
      lng: -123.1150,
      popupText: "Capilano Suspension Bridge Park",
    },
    {
      id: "queen-elizabeth",
      lat: 49.2422,
      lng: -123.1150,
      popupText: "Queen Elizabeth Park: Gardens & City Views",
    },
    {
      id: "lynn-canyon",
      lat: 49.3436,
      lng: -123.0178,
      popupText: "Lynn Canyon Park: Free Suspension Bridge & Rainforest",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Car-Free Adventures in Vancouver</title>
        <meta
          name="description"
          content="A thorough guide on how to explore Vancouver without a car, complete with transit tips, top destinations, local regulations, and FAQs."
        />
        <meta
          name="keywords"
          content="Vancouver, Car-Free, Public Transport, Travel Tips, Tourism"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/car-free-adventures-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Car-Free Adventures in Vancouver"
        />
        <meta
          property="og:description"
          content="Discover the best ways to explore Vancouver without a car. Public transit, biking, and walking routes, plus top destinations and FAQs."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/car-free-adventures-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/car-free-adventures-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Car-Free Adventures in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Plan a detailed, sustainable Vancouver adventure. Transit tips, local regulations, top landmarks, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/car-free-adventures-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Vancouver Skyline"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is known worldwide for its stunning mountain-meets-ocean
              scenery, vibrant neighborhoods, and eco-conscious spirit. Luckily,
              you don’t need a car to enjoy it all. With an extensive public
              transit network, bike-friendly paths, and walkable districts,
              Vancouver makes it easy to explore sustainably. This guide covers
              everything you need to plan your own car-free adventure—whether
              you’re a local looking to explore new corners of the city or a
              traveler seeking an authentic urban getaway.
            </p>

            {/* SECTION: Getting Around Car-Free */}
            <h2 id="section-getting-around">Getting Around Car-Free</h2>
            <p>
              <strong>Public Transit:</strong> The backbone of Vancouver’s
              car-free lifestyle is the TransLink system, which includes
              SkyTrain (rapid transit), buses, and the SeaBus ferry. Purchase a
              Compass Card or tap a contactless credit/debit card to ride
              seamlessly across all modes.
            </p>
            <p>
              <strong>Biking:</strong> Vancouver boasts a well-developed network
              of bike lanes and greenways that make cycling safe and efficient.
              You can bring bikes on most public transit (buses have bike racks
              up front; SkyTrain and SeaBus allow bikes during non-peak hours).
              Mobi bike-share stations can be found throughout the city for
              short-term rentals.
            </p>
            <p>
              <strong>Walking & Ferries:</strong> Many neighborhoods are highly
              walkable, and downtown attractions are often within a comfortable
              stroll of one another. In False Creek, hop on the Aquabus or False
              Creek Ferries to reach waterfront spots like Granville Island
              without stepping into a car.
            </p>

            {/* SECTION: Top Car-Free Destinations */}
            <h2 id="section-top-destinations">Top Car-Free Destinations</h2>
            <p>
              Vancouver is teeming with attractions and natural beauty that are
              easily accessible by foot, bike, or transit. Below are some of the
              city’s highlights:
            </p>

            {/* Stanley Park */}
            <p
              ref={(el) => setSpotRef("stanley-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>1. Stanley Park</strong>{" "}
              <Marker
                coordY={49.3042}
                coordX={-123.1443}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.3042, -123.1443)  
              Vancouver’s iconic urban oasis offers
              towering cedar trees, a famous seawall for walking or biking, and
              attractions like the Vancouver Aquarium. Access it by foot or
              bike from downtown, or ride bus #19.
            </p>

            {/* Granville Island */}
            <p
              ref={(el) => setSpotRef("granville-island", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>2. Granville Island</strong>{" "}
              <Marker
                coordY={49.2726}
                coordX={-123.1352}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.2726, -123.1352)  
              A bustling public market brimming with
              fresh produce, artisanal goods, and live entertainment. Reach it
              by bus #50 from downtown or take a scenic trip on the Aquabus.
            </p>

            {/* Gastown */}
            <p
              ref={(el) => setSpotRef("gastown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>3. Gastown</strong>{" "}
              <Marker
                coordY={49.2844}
                coordX={-123.1090}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.2844, -123.1090)  
              Vancouver’s oldest neighborhood, featuring
              cobblestone streets, the famous Steam Clock, and Victorian
              architecture. It’s a short walk from Waterfront Station.
            </p>

            {/* Kitsilano Beach */}
            <p
              ref={(el) => setSpotRef("kitsilano-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>4. Kitsilano Beach</strong>{" "}
              <Marker
                coordY={49.2739}
                coordX={-123.1551}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.2739, -123.1551)  
              Popular for sunbathing, beach volleyball,
              and scenic views of downtown. Ride the #2 or #22 bus from
              downtown, or bike across Burrard Bridge to get there.
            </p>

            {/* Grouse Mountain */}
            <p
              ref={(el) => setSpotRef("grouse-mountain", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>5. Grouse Mountain</strong>{" "}
              <Marker
                coordY={49.3772}
                coordX={-123.0816}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.3772, -123.0816)  
              Enjoy panoramic city views, hiking, and
              winter sports. Take the SeaBus to Lonsdale Quay, then bus #236.
            </p>

            {/* Capilano Suspension Bridge */}
            <p
              ref={(el) => setSpotRef("capilano", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>6. Capilano Suspension Bridge Park</strong>{" "}
              <Marker
                coordY={49.3429}
                coordX={-123.1150}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.3429, -123.1150)  
              A thrilling 140-metre-long suspension bridge
              over a rainforest canyon. Reachable via a free shuttle from
              downtown (seasonal) or bus #236 from Lonsdale Quay.
            </p>

            {/* Queen Elizabeth Park */}
            <p
              ref={(el) => setSpotRef("queen-elizabeth", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>7. Queen Elizabeth Park</strong>{" "}
              <Marker
                coordY={49.2422}
                coordX={-123.1150}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.2422, -123.1150)  
              The highest point in Vancouver, offering
              panoramic views, lush gardens, and the Bloedel Conservatory. Take
              the Canada Line to King Edward Station, then walk or catch bus
              #33.
            </p>

            {/* Lynn Canyon */}
            <p
              ref={(el) => setSpotRef("lynn-canyon", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>8. Lynn Canyon Park</strong>{" "}
              <Marker
                coordY={49.3436}
                coordX={-123.0178}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (49.3436, -123.0178)  
              A free suspension bridge and forest trails
              for hiking, swimming holes, and picnicking. Access via SeaBus plus
              bus #228 or #229.
            </p>

            {/* SECTION: Local Regulations / Best Practices */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              <strong>Transit Etiquette:</strong> Use a valid fare (Compass
              Card, cash, or contactless payment) and respect priority seating
              for seniors or people with disabilities. Don’t block doors or
              aisles.
            </p>
            <p>
              <strong>Bike Laws:</strong> Helmets are mandatory in British
              Columbia. Use bike lights at night, follow traffic signals, and
              avoid cycling on sidewalks unless explicitly permitted. Electric
              scooters are in a pilot phase, so check local guidelines.
            </p>
            <p>
              <strong>Pedestrian Safety:</strong> Cross at intersections or
              crosswalks, as jaywalking is technically prohibited. Most drivers
              will stop for pedestrians, but always make eye contact before
              crossing.
            </p>
            <p>
              <strong>Park Regulations:</strong> Observe posted signs regarding
              wildlife or trail closures. Alcohol consumption is prohibited in
              most public spaces unless otherwise indicated. Always carry out
              any trash and respect “Leave No Trace” principles to keep
              Vancouver’s parks pristine.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a car at all to explore Vancouver?</h3>
              <p>
                Not really. Vancouver’s neighborhoods and attractions are well
                connected by transit, bike routes, and walking paths. A car can
                be handy for day trips to outlying areas, but within the city
                core and nearby North Shore, going car-free is both feasible and
                enjoyable.
              </p>

              <h3>2. What if it rains during my trip?</h3>
              <p>
                Vancouver does see its fair share of rain, especially outside
                summer months. Bring a waterproof jacket or umbrella, and
                consider shifting to indoor attractions like museums or
                galleries. Public transit runs rain or shine, and many stops
                offer covered shelters.
              </p>

              <h3>3. Are there late-night transit options?</h3>
              <p>
                Yes. While SkyTrain and SeaBus typically wrap up around midnight,
                NightBus routes operate throughout the city. Always check
                TransLink’s schedule so you don’t miss the last train or bus.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Exploring Vancouver without a car is not just practical—it’s a
              chance to immerse yourself in local culture, discover hidden gems,
              and travel more sustainably. From Stanley Park’s forests to the
              bustling Granville Island Market, each corner of the city is
              reachable with a little planning, comfortable walking shoes, and
              an adventurous spirit. Ready for your Vancouver adventure? Hop on
              transit, rent a bike, or simply follow your feet. You’ll be
              rewarded with unforgettable experiences minus the traffic and
              parking headaches.
            </p>
            <p>
              <em>
                Disclaimer: Always check official resources for up-to-date
                schedules, trail closures, and any safety advisories. Conditions
                can change due to weather or local regulations.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}